import { CommonModule } from '@angular/common';
import { Component, computed, ElementRef, effect, HostListener, inject, input, output, signal } from '@angular/core';
import { MessageService, ToastMessageOptions } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ValidationErrorSection } from './validation-error-summary.model';

@Component({
  selector: 'app-validation-error-summary',
  standalone: true,
  providers: [MessageService],
  imports: [CommonModule, ToastModule],
  templateUrl: './validation-error-summary.component.html',
  styleUrl: './validation-error-summary.component.scss',
})
export class ValidationErrorSummaryComponent {
  private readonly validationToastKey = 'validation-error-summary';
  private readonly messageService = inject(MessageService);
  private activeToastMessage?: ToastMessageOptions;

  sections = input<ValidationErrorSection[]>([]);
  closed = output<void>();
  detailsExpanded = signal(false);
  activeSections = signal<ValidationErrorSection[]>([]);
  errorCount = computed(() => this.activeSections().reduce((count, section) => count + section.errors.length, 0));

  private hostElement = inject(ElementRef<HTMLElement>);

  constructor() {
    effect(() => {
      const sections = this.sections();
      this.messageService.clear(this.validationToastKey);
      this.activeSections.set(sections);

      if (sections.length > 0) {
        this.detailsExpanded.set(false);
        const toastMessage: ToastMessageOptions = {
          key: this.validationToastKey,
          severity: 'warn',
          summary: 'Attenzione',
          detail: `I seguenti campi obbligatori non sono compilati.`,
          sticky: true,
          closable: true,
          closeIcon: 'pi-times',
          data: { sections },
        };
        this.activeToastMessage = toastMessage;
        this.messageService.add(toastMessage);
        queueMicrotask(() => this.scrollToFirstError());
      } else {
        this.activeToastMessage = undefined;
      }
    });
  }

  handleToastClose(event: { message?: ToastMessageOptions | null }): void {
    if (event.message !== this.activeToastMessage) return;

    this.activeToastMessage = undefined;
    this.closed.emit();
  }

  @HostListener('document:input', ['$event'])
  handleFieldInput(event: Event): void {
    this.removeFilledField(event.target);
  }

  @HostListener('document:change', ['$event'])
  handleFieldChange(event: Event): void {
    this.removeFilledField(event.target);
  }

  selectError(fieldId?: string): void {
    this.detailsExpanded.set(false);

    const field = fieldId
      ? document.getElementById(fieldId)
      : this.findFirstInvalidField();

    this.scrollToField(field);
  }

  toggleDetails(): void {
    this.detailsExpanded.update(expanded => !expanded);
  }

  private scrollToFirstError(): void {
    const firstError = this.sections().flatMap(section => section.errors)[0];
    const field = firstError?.fieldId
      ? document.getElementById(firstError.fieldId)
      : this.findFirstInvalidField();

    this.scrollToField(field);
  }

  private findFirstInvalidField(): HTMLElement | null {
    const invalidField = document.querySelector<HTMLElement>(
      '[aria-invalid="true"], .p-invalid, .ng-invalid',
    );

    return invalidField && !this.hostElement.nativeElement.contains(invalidField)
      ? invalidField
      : null;
  }

  private removeFilledField(eventTarget: EventTarget | null): void {
    if (!this.activeToastMessage || !(eventTarget instanceof HTMLElement)) return;

    const activeSections = this.activeSections();

    const matchingError = this.findErrorForTarget(eventTarget, activeSections);
    if (!matchingError || !this.hasMeaningfulValue(eventTarget)) return;

    setTimeout(() => {
      if (!this.isFieldValid(eventTarget)) return;

      const currentSections = this.activeSections();

      const sections = currentSections
        .map(section => ({
          ...section,
          errors: section.errors.filter(error => error.fieldId !== matchingError.fieldId),
        }))
        .filter(section => section.errors.length > 0);

      this.updateActiveSections(sections);
    }, 0);
  }

  private findErrorForTarget(
    target: HTMLElement,
    sections: ValidationErrorSection[],
  ): { fieldId: string } | undefined {
    let current: HTMLElement | null = target;

    while (current) {
      const currentId = current.id;
      if (currentId && sections.some(section => section.errors.some(error => error.fieldId === currentId))) {
        return { fieldId: currentId };
      }

      current = current.parentElement;
    }

    return undefined;
  }

  private hasMeaningfulValue(element: HTMLElement): boolean {
    const control = this.findFormControl(element);
    if (!control) return Boolean(element.textContent?.trim());

    if (control instanceof HTMLInputElement && (control.type === 'checkbox' || control.type === 'radio')) {
      return control.checked;
    }

    return Boolean(control.value?.trim());
  }

  private isFieldValid(element: HTMLElement): boolean {
    if (!this.hasMeaningfulValue(element)) return false;

    let current: HTMLElement | null = element;
    while (current) {
      if (
        current.getAttribute('aria-invalid') === 'true' ||
        current.classList.contains('p-invalid') ||
        current.classList.contains('ng-invalid')
      ) {
        return false;
      }

      current = current.parentElement;
    }

    return true;
  }

  private findFormControl(element: HTMLElement): HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null {
    if (element instanceof HTMLInputElement || element instanceof HTMLSelectElement || element instanceof HTMLTextAreaElement) {
      return element;
    }

    return element.querySelector<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
      'input, select, textarea',
    );
  }

  private updateActiveSections(sections: ValidationErrorSection[]): void {
    if (!this.activeToastMessage) return;

    if (sections.length === 0) {
      this.messageService.clear(this.validationToastKey);
      this.activeToastMessage = undefined;
      this.closed.emit();
      return;
    }

    this.activeSections.set(sections);
    this.activeToastMessage.data = {
      ...this.activeToastMessage.data,
      sections,
    };
  }

  private scrollToField(field: HTMLElement | null): void {
    if (!field) return;

    field.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const focusTarget = field.matches('input, select, textarea, button, [tabindex]')
      ? field
      : field.querySelector<HTMLElement>('input, select, textarea, button, [tabindex]');
    focusTarget?.focus({ preventScroll: true });
  }
}